import {Component, effect, inject, Input, input, InputSignal, output, OutputEmitterRef, signal, WritableSignal, ChangeDetectionStrategy} from '@angular/core';
import {TRANSPARENT_1PX_GIF} from '../../constants/ui/transparent1px-gif.constant';
import {HttpClient} from '@angular/common/http';
import {CacheService} from '../../services/cache/cache.service';
import {catchError, take} from 'rxjs/operators';
import {Observable} from 'rxjs/internal/Observable';
import {lastValueFrom, of} from 'rxjs';
import {AuthService} from '../../services/auth/auth.service';
import {BasicSubscribableComponent} from '../dummy-components/basic-subscribable-component';

@Component({
    selector: 'app-image',
    templateUrl: './image.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './image.component.css'
})
export class ImageComponent extends BasicSubscribableComponent {
    public alt: InputSignal<string> = input.required<string>();
    public ariaHidden: InputSignal<boolean> = input<boolean>(false);
    public lazyLoading: InputSignal<boolean> = input<boolean>(true);
    public height: InputSignal<number | undefined> = input<number>();
    public width: InputSignal<number | undefined> = input<number>();
    public defaultPlaceholderImageSrc: string = TRANSPARENT_1PX_GIF;
    public imageHasLoaded: OutputEmitterRef<void> = output<void>();
    public imageHasError: OutputEmitterRef<void> = output<void>();
    public imageObjectFit: InputSignal<'fill' | 'contain' | 'cover' | 'none' | 'scale-down'> = input<'fill' | 'contain' | 'cover' | 'none' | 'scale-down'>('none');
    protected imageSrcSignal: WritableSignal<string | undefined>;
    private readonly srcObjectSignal: WritableSignal<string | undefined> = signal(undefined);
    private readonly authService = inject(AuthService);
    private readonly http = inject(HttpClient);
    private readonly cacheService = inject(CacheService);

    @Input({ required: true }) public set src(src: string) {
        this.srcObjectSignal.set(src);
    }

    public constructor() {
        super();
        this.imageSrcSignal = signal(undefined);
        effect(() => {
            const imageSrc = this.srcObjectSignal();
            if (imageSrc) {
                this.fetchImage()
                    .then((objectUrl: string) => this.imageSrcSignal.set(objectUrl))
                    .catch(() => this.handleImageError());
            } else {
                this.cleanUpImageSrc();
            }
        });
    }

    public handleImageLoaded(): void {
        this.imageHasLoaded.emit();

        this.cleanUpImageSrc();
    }

    public handleImageError(): void {
        this.imageHasError.emit();

        this.cleanUpImageSrc();

        this.imageSrcSignal.set(this.defaultPlaceholderImageSrc);
    }

    private cleanUpImageSrc(): void {
        const imageSrc = this.imageSrcSignal();
        if (imageSrc) {
            URL.revokeObjectURL(imageSrc);
        }
    }

    private async fetchImage(): Promise<string> {
        const bearer = await this.authService.getBearer();
        if (bearer) {
            const srcObject = this.srcObjectSignal() as string;
            if (this.cacheService.hasImageCached(srcObject)) {
                return this.cacheService.getImageFromCache(srcObject) as string;
            } else {
                const response = this.http.get(srcObject, {
                    headers: {
                        Authorization: bearer,
                    },
                    responseType: 'blob'
                });

                const blob = await lastValueFrom(response
                    .pipe(
                        take(1),
                        catchError((error): Observable<never> => {
                            if (error) {
                                this.handleImageError();
                            }

                            return of();
                        })
                    ));
                const reader = new FileReader;

                reader.onload = (): void => {
                    this.cacheService.addImage(srcObject, reader.result as string);
                };

                reader.readAsDataURL(blob);
                return URL.createObjectURL(blob);
            }
        } else {
            return '';
        }
    }
}
